import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { ThreePanelLayout, ThreePanelHandle } from '../components/notebook/ThreePanelLayout';
import { CellManager } from '../components/notebook/CellManager';
import { FileExplorer } from '../components/notebook/FileExplorer';
import { NotebookCell } from '../components/notebook/NotebookEditor';
import { TableOfContents } from '../components/notebook/TableOfContents';
import { CommandPalette, PaletteCommand } from '../components/notebook/CommandPalette';
import { ShortcutsModal } from '../components/notebook/ShortcutsModal';
import { VariableInspector } from '../components/notebook/VariableInspector';
import { ResultsPanel } from '../components/notebook/ResultsPanel';
import { NotebookTabBar, NotebookTab, NotebookTabKind } from '../components/notebook/NotebookTabBar';
import { TextFileEditor } from '../components/notebook/TextFileEditor';
import { isEditableTextFile } from '../components/notebook/codemirror/extensions/languageMap';
import { getNotebookService, disposeNotebookService } from '../services/NotebookService';
import { useSidebar } from '../contexts/SidebarContext';
import GPUMetricsHeader from '../components/notebook/gpu/GPUMetricsHeader';
import AIAssistantCell from '../components/notebook/AIAssistantCell';
import { getApiUrl, getWebSocketUrl } from '../utils/api';
import { useStrategyAssistant } from '../hooks/useStrategyAssistant';

/**
 * Native Jupyter notebook page with PropAlgos theme.
 * Replaces iframe-based implementation with direct API integration.
 */
const NativeNotebookPage: React.FC = () => {
  const { notebookId } = useParams<{ notebookId: string }>();
  const navigate = useNavigate();
  const { setIsCollapsed } = useSidebar();

  // Refs for notebook context — updated as session/notebook state changes.
  // The getNotebookContext callback closes over these refs so the hook always reads current values.
  const notebookContextRefs = useRef<{
    sessionId: string | null;
    jupyterToken: string | null;
    currentNotebook: string | null;
  }>({ sessionId: null, jupyterToken: null, currentNotebook: null });

  const getNotebookContext = useCallback(async () => {
    const { sessionId: sid, jupyterToken: tok, currentNotebook: nb } = notebookContextRefs.current;
    if (!sid || !tok) return null;
    try {
      const service = getNotebookService({ sessionId: sid, token: tok });
      const variables = await service.getVariables();
      const dirPath = nb ? nb.substring(0, nb.lastIndexOf('/')) || '' : '';
      let filesInDirectory: string[] | undefined;
      if (dirPath) {
        try {
          const listing = await service.listFiles(dirPath);
          filesInDirectory = listing?.content?.map((f: any) => f.name) || undefined;
        } catch { /* dir listing failed */ }
      }
      return {
        notebookPath: nb || undefined,
        workingDirectory: dirPath || undefined,
        filesInDirectory,
        variables: variables.map(v => ({ name: v.name, type: v.type, value: v.value, shape: v.shape })),
      };
    } catch {
      return null;
    }
  }, []);

  const {
    phase: aiPhase,
    analysis: aiAnalysis,
    answers: aiAnswers,
    generatedCode: aiGeneratedCode,
    validationResult: aiValidationResult,
    error: aiError,
    isLoading: aiIsLoading,
    sessionUsage,
    analyzeIntent,
    submitAnswer,
    finishQuestioning,
    validateCode,
    fixCode,
    prepareForNotebook,
    reset: resetAI,
  } = useStrategyAssistant(getNotebookContext);

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [jupyterToken, setJupyterToken] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryTick, setRetryTick] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const [currentNotebook, setCurrentNotebook] = useState<string | null>(null);
  const [cells, setCells] = useState<NotebookCell[]>([]);
  const [kernelStatus, setKernelStatus] = useState<string>('unknown');
  const [instanceDetails, setInstanceDetails] = useState<any>(null);
  const [loadingStage, setLoadingStage] = useState<string>('Connecting to instance...');
  const [loadingProgress, setLoadingProgress] = useState<number>(0);
  const [currentStage, setCurrentStage] = useState<string>('connecting');
  const [completedStages, setCompletedStages] = useState<Set<string>>(new Set());
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveStatusMessage, setSaveStatusMessage] = useState<string | null>(null);
  const [cloudSaveStatus, setCloudSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [showSaveAsDialog, setShowSaveAsDialog] = useState(false);
  const [saveAsDirs, setSaveAsDirs] = useState<Array<{name: string; path: string}>>([]);
  const [saveAsBrowsePath, setSaveAsBrowsePath] = useState('my_data');
  const [saveAsLoadingDirs, setSaveAsLoadingDirs] = useState(false);
  const [saveAsFileName, setSaveAsFileName] = useState('');
  const [fileExplorerRefreshTrigger, setFileExplorerRefreshTrigger] = useState(0);
  const [liveCells, setLiveCells] = useState<NotebookCell[]>([]);
  const [rightPanelTab, setRightPanelTab] = useState<'toc' | 'variables' | 'results'>('toc');
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [variableRefreshTrigger, setVariableRefreshTrigger] = useState(0);

  // Multi-notebook tabs
  const [openTabs, setOpenTabs] = useState<NotebookTab[]>([]);
  const [activeTabPath, setActiveTabPath] = useState<string | null>(null);
  // Non-null when the active tab is a text file. Notebook tabs use `currentNotebook` instead.
  const [activeTextFilePath, setActiveTextFilePath] = useState<string | null>(null);

  // Proxy auth: establish cookie for iframe-based proxy apps (Streamlit, Bokeh, etc.)
  const [proxyAuthCode, setProxyAuthCode] = useState<string | null>(null);
  const [proxyOrigin, setProxyOrigin] = useState<string | null>(null);

  // Ref to store the addCell function from CellManager (for AI code insertion)
  const addCellRef = useRef<((code: string, type: 'code' | 'markdown') => void) | null>(null);
  // Ref to hold cells between Save As button click and dialog submit
  const pendingSaveAsCellsRef = useRef<NotebookCell[] | null>(null);
  // Concurrency guard for save operations
  const isSavingRef = useRef(false);
  // Last cells passed to save (for retry)
  const lastSaveCellsRef = useRef<NotebookCell[] | null>(null);
  // Panel toggle control ref
  const panelControlRef = useRef<ThreePanelHandle | null>(null);

  // Keep AI context refs in sync with current state
  useEffect(() => {
    notebookContextRefs.current = { sessionId, jupyterToken, currentNotebook };
  }, [sessionId, jupyterToken, currentNotebook]);

  // Stable notebook service reference for variable inspector
  const activeNotebookService = useMemo(() => {
    if (sessionId && jupyterToken) {
      return getNotebookService({ sessionId, token: jupyterToken });
    }
    return null;
  }, [sessionId, jupyterToken]);

  // Kernel control handlers (shared between header and toolbar)
  const handleInterruptKernel = useCallback(async () => {
    try {
      const service = getNotebookService({ sessionId: sessionId!, token: jupyterToken || undefined });
      await service.interruptKernel();
    } catch (e: any) {
      console.warn('Interrupt failed:', e.message);
    }
  }, [sessionId, jupyterToken]);

  const handleRestartKernel = useCallback(async () => {
    if (!window.confirm('Restart kernel? All variables will be lost.')) return;
    try {
      setKernelStatus('restarting');
      const service = getNotebookService({ sessionId: sessionId!, token: jupyterToken || undefined });
      await service.restartKernel();
    } catch (e: any) {
      console.warn('Restart failed:', e.message);
    }
  }, [sessionId, jupyterToken]);

  // Global keyboard shortcuts for panel toggles
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
        e.preventDefault();
        panelControlRef.current?.toggleLeft();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'j') {
        e.preventDefault();
        panelControlRef.current?.toggleRight();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Build command palette commands
  const paletteCommands: PaletteCommand[] = React.useMemo(() => [
    { id: 'run-all', label: 'Run All Cells', shortcut: '', category: 'Run', action: () => {} },
    { id: 'toggle-left', label: 'Toggle File Explorer', shortcut: 'Cmd+B', category: 'View', action: () => panelControlRef.current?.toggleLeft() },
    { id: 'toggle-right', label: 'Toggle Right Panel', shortcut: 'Cmd+J', category: 'View', action: () => panelControlRef.current?.toggleRight() },
    { id: 'shortcuts', label: 'Show Keyboard Shortcuts', shortcut: 'H', category: 'Help', action: () => setShowShortcutsModal(true) },
    { id: 'interrupt', label: 'Interrupt Kernel', category: 'Kernel', action: handleInterruptKernel },
    { id: 'restart', label: 'Restart Kernel', category: 'Kernel', action: handleRestartKernel },
    { id: 'toc', label: 'Show Table of Contents', category: 'View', action: () => { panelControlRef.current?.toggleRight(); setRightPanelTab('toc'); } },
    { id: 'variables', label: 'Show Variable Inspector', category: 'View', action: () => { panelControlRef.current?.toggleRight(); setRightPanelTab('variables'); } },
    { id: 'ask-ai', label: 'Ask AI About Code', shortcut: 'Cmd+L', category: 'AI', action: () => {
      document.dispatchEvent(new CustomEvent('propalgos:toggle-inline-ai'));
    }},
  ], [handleInterruptKernel, handleRestartKernel]);

  // Derived read-only state: any path under samples/ is read-only
  const isReadOnly = currentNotebook
    ? (currentNotebook === 'samples' || currentNotebook.startsWith('samples/'))
    : false;

  // Ref to carry pending notebook path across the new-session → actual-ID navigate boundary.
  // sessionStorage is the persistent fallback, but this ref survives the useEffect re-run
  // without depending on key matching between effect invocations.
  const pendingAutoOpenRef = useRef<string | null>(null);
  const wasAutoOpenedRef = useRef(false);

  // Poll my_data/ until rclone mount is ready, then refresh the file explorer.
  // Called from both init paths so manual launches see storage too.
  const waitForMyData = useCallback(async (service: ReturnType<typeof getNotebookService>) => {
    const BACKOFF = [1000, 1000, 2000, 2000, 3000, 4000, 5000, 7000];
    for (let i = 0; i < BACKOFF.length; i++) {
      try {
        await service.listFiles('my_data');
        setFileExplorerRefreshTrigger(prev => prev + 1);
        return;
      } catch {
        if (i < BACKOFF.length - 1) {
          await new Promise(r => setTimeout(r, BACKOFF[i]));
        }
      }
    }
  }, []);

  // Fetch a fresh proxy auth code from the backend.
  // Reusable: called on session init and on-demand when proxy content appears.
  const getProxyAuthCode = useCallback(async (): Promise<string | null> => {
    if (!sessionId) return null;
    const token = localStorage.getItem('authToken');
    if (!token) return null;

    try {
      const apiUrl = getApiUrl();
      const res = await fetch(`${apiUrl}/api/v1/notebooks/proxy-auth/${sessionId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (data) {
        setProxyAuthCode(data.auth_code);
        setProxyOrigin(data.proxy_origin || null);
        return data.auth_code;
      }
    } catch (e) {
      console.warn('Failed to fetch proxy auth code:', e);
    }
    return null;
  }, [sessionId]);

  // Establish proxy auth when session is ready — enables cookie-based auth
  // for embedded apps (Streamlit, Bokeh, etc.) loaded in iframes.
  useEffect(() => {
    if (!sessionId) return;
    getProxyAuthCode();
  }, [sessionId, getProxyAuthCode]);

  /**
   * Segment AI-generated code into logical notebook cells.
   * Returns array of {type, content} objects.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const segmentCodeIntoCells = useCallback((code: string): Array<{type: 'code' | 'markdown', content: string}> => {
    const cells: Array<{type: 'code' | 'markdown', content: string}> = [];
    const lines = code.split('\n');

    // 1. Extract leading comments as markdown description
    const leadingComments: string[] = [];
    let codeStartIndex = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line.startsWith('#') && !line.startsWith('#!')) {
        leadingComments.push(line.replace(/^#+\s*/, ''));
      } else if (line === '') {
        // Skip empty lines at the start
        if (leadingComments.length > 0) {
          codeStartIndex = i + 1;
          break;
        }
      } else {
        codeStartIndex = i;
        break;
      }
    }

    if (leadingComments.length > 0) {
      const title = leadingComments[0];
      const description = leadingComments.slice(1).join('\n');
      cells.push({
        type: 'markdown',
        content: `# ${title}${description ? '\n\n' + description : ''}`
      });
    }

    // 2. Parse remaining code into segments
    const remainingLines = lines.slice(codeStartIndex);
    const importLines: string[] = [];
    const functionBlocks: string[] = [];
    const mainCodeLines: string[] = [];

    let currentBlock: string[] = [];
    let inFunction = false;
    let functionIndent = 0;

    for (let i = 0; i < remainingLines.length; i++) {
      const line = remainingLines[i];
      const trimmedLine = line.trim();

      // Check for import statements
      if ((trimmedLine.startsWith('import ') || trimmedLine.startsWith('from ')) && !inFunction) {
        importLines.push(line);
        continue;
      }

      // Check for function/class definition start
      if ((trimmedLine.startsWith('def ') || trimmedLine.startsWith('class ')) && !inFunction) {
        // Save any accumulated main code first
        if (currentBlock.length > 0) {
          const blockContent = currentBlock.join('\n').trim();
          if (blockContent) {
            mainCodeLines.push(...currentBlock);
          }
          currentBlock = [];
        }

        inFunction = true;
        functionIndent = line.search(/\S/);
        currentBlock = [line];
        continue;
      }

      // Inside a function/class
      if (inFunction) {
        const currentIndent = line.search(/\S/);
        // Check if we've exited the function (non-empty line with same or less indent)
        if (trimmedLine !== '' && currentIndent !== -1 && currentIndent <= functionIndent &&
            !trimmedLine.startsWith('def ') && !trimmedLine.startsWith('class ') &&
            !trimmedLine.startsWith('@')) {
          // End of function - save it
          functionBlocks.push(currentBlock.join('\n'));
          currentBlock = [line];
          inFunction = false;
        } else {
          currentBlock.push(line);
        }
      } else {
        currentBlock.push(line);
      }
    }

    // Save any remaining block
    if (currentBlock.length > 0) {
      const blockContent = currentBlock.join('\n').trim();
      if (blockContent) {
        if (inFunction) {
          functionBlocks.push(currentBlock.join('\n'));
        } else {
          mainCodeLines.push(...currentBlock);
        }
      }
    }

    // 3. Create cells from segments
    if (importLines.length > 0) {
      cells.push({
        type: 'code',
        content: importLines.join('\n')
      });
    }

    // Add function blocks as separate cells
    for (const funcBlock of functionBlocks) {
      const trimmed = funcBlock.trim();
      if (trimmed) {
        cells.push({
          type: 'code',
          content: trimmed
        });
      }
    }

    // Add main code
    const mainCode = mainCodeLines.join('\n').trim();
    if (mainCode) {
      cells.push({
        type: 'code',
        content: mainCode
      });
    }

    // Fallback: if no cells were created, just add the whole code
    if (cells.length === 0) {
      cells.push({
        type: 'code',
        content: code.trim()
      });
    }

    return cells;
  }, []);

  // Handle AI-generated code insertion - now receives pre-segmented cells from AIAssistantCell
  const handleInsertAICode = useCallback((content: string, type: 'code' | 'markdown' = 'code') => {
    if (!addCellRef.current) return;
    addCellRef.current(content, type);
  }, []);

  // Collapse sidebar when notebook loads for cleaner layout
  useEffect(() => {
    // Collapse the main dashboard sidebar
    setIsCollapsed(true);
    
    // Restore on unmount (when leaving the notebook page)
    return () => {
      setIsCollapsed(false);
    };
  }, [setIsCollapsed]);

  // Cleanup notebook service and clear cached kernel info on unmount
  useEffect(() => {
    return () => {
      // Dispose notebook service if it exists
      if (sessionId && jupyterToken) {
        // Async cleanup in a sync cleanup function
        (async () => {
          try {
            const service = getNotebookService({ sessionId, token: jupyterToken });
            await service.dispose();
            disposeNotebookService(sessionId);
          } catch (e) {
            console.log('Error disposing notebook service:', e);
          }
        })();
      }
      
      // Clear any cached kernel info from session storage
      if (notebookId) {
        sessionStorage.removeItem(`notebook_kernel_${notebookId}`);
        sessionStorage.removeItem(`notebook_token_${notebookId}`);
      }
    };
  }, [sessionId, jupyterToken, notebookId]);

  // Auto-open a sample notebook after session is ready.
  // Copies from read-only FUSE mount to writable workspace, then loads into editor.
  // Falls back to loading directly from samples path if copy fails.
  //
  // samplesPath can be:
  //   - Plain string: "samples/strategy_templates/Strategy_01_Momentum.ipynb"
  //   - JSON config:  { "path": "samples/.../notebook.ipynb", "directoryPath": "samples/crypto_rsi_scanner" }
  const autoOpenNotebook = useCallback(async (service: ReturnType<typeof getNotebookService>, samplesPath: string) => {
    // Parse the samplesPath — could be JSON with directoryPath or a plain string
    let notebookPath: string;
    let directoryPath: string | undefined;
    try {
      const parsed = JSON.parse(samplesPath);
      notebookPath = parsed.path;
      directoryPath = parsed.directoryPath;
    } catch {
      notebookPath = samplesPath;
    }

    const cleanupPendingKeys = () => {
      // Only clean up pending keys after successful load.
      // This is the ONLY place these keys should be removed — keeping
      // them alive through timeouts/retries so retry attempts still work.
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('pending_notebook_for_')) {
          sessionStorage.removeItem(key);
        }
      }
      sessionStorage.removeItem('pending_sample_notebook');
    };

    const loadCells = (notebook: any, path: string) => {
      if (!notebook?.cells) return false;
      const loadedCells: NotebookCell[] = notebook.cells.map((cell: any) => ({
        id: cell.metadata?.id || Math.random().toString(36).substring(2, 11),
        type: cell.cell_type === 'code' ? 'code' : 'markdown',
        source: Array.isArray(cell.source) ? cell.source.join('') : cell.source,
        outputs: cell.outputs || [],
        execution_count: cell.execution_count || null,
        metadata: cell.metadata || {},
      }));
      setCells(loadedCells);
      setCurrentNotebook(path);
      // Add tab for auto-opened notebook
      const tabTitle = path.split('/').pop() || path;
      setOpenTabs(prev => prev.some(t => t.path === path) ? prev : [...prev, { path, title: tabTitle, isDirty: false }]);
      setActiveTabPath(path);
      const pathIsReadOnly = path === 'samples' || path.startsWith('samples/');
      setSaveStatus(pathIsReadOnly ? 'idle' : 'saved');
      cleanupPendingKeys();
      return true;
    };

    // If path starts with "samples/", wait for the FUSE mount to appear.
    // Probe the directory via Jupyter Contents API — 404 means not mounted yet.
    if (notebookPath.startsWith('samples/')) {
      const MAX_ATTEMPTS = 8;
      const BACKOFF = [1000, 1000, 2000, 2000, 3000, 4000, 5000, 7000];
      for (let i = 0; i < MAX_ATTEMPTS; i++) {
        try {
          await service.listFiles('samples');
          break; // mount is ready
        } catch {
          if (i === MAX_ATTEMPTS - 1) {
            console.warn('[auto-open] FUSE mount not available after retries');
            setSaveStatusMessage('Sample files are still loading. Try reopening from the file explorer in a moment.');
            setTimeout(() => setSaveStatusMessage(null), 8000);
            return;
          }
          console.log(`[auto-open] FUSE mount not ready, retry ${i + 1}/${MAX_ATTEMPTS} in ${BACKOFF[i]}ms`);
          await new Promise(r => setTimeout(r, BACKOFF[i]));
        }
      }
    }

    // Wait for my_data/ rclone mount the same way we wait for the FUSE samples/ mount
    {
      const MAX_ATTEMPTS = 8;
      const BACKOFF = [1000, 1000, 2000, 2000, 3000, 4000, 5000, 7000];
      let myDataReady = false;
      for (let i = 0; i < MAX_ATTEMPTS; i++) {
        try {
          await service.listFiles('my_data');
          myDataReady = true;
          break;
        } catch {
          if (i === MAX_ATTEMPTS - 1) {
            console.warn('[auto-open] my_data mount not available after retries');
          } else {
            console.log(`[auto-open] my_data not ready, retry ${i + 1}/${MAX_ATTEMPTS} in ${BACKOFF[i]}ms`);
            await new Promise(r => setTimeout(r, BACKOFF[i]));
          }
        }
      }
      if (!myDataReady) {
        setSaveStatusMessage('Storage is still mounting. Try reopening from the file explorer in a moment.');
        setTimeout(() => setSaveStatusMessage(null), 8000);
        return;
      }
    }

    // Both mounts ready — smart copy (hash-based dedup) then fallback to direct load
    try {
      let copiedNotebookPath: string;
      let copyAction: string;

      if (directoryPath) {
        // Directory-based sample: smart-copy entire directory, then derive notebook path
        const dirResult = await service.smartCopyDirectoryTo(directoryPath, 'my_data');
        const notebookFilename = notebookPath.split('/').pop() || '';
        copiedNotebookPath = `${dirResult.destDir}/${notebookFilename}`;
        copyAction = dirResult.copied.length > 0 ? 'copy-fresh' : 'open-existing';
        if (dirResult.failed.length > 0) {
          console.warn('[auto-open] Partial directory copy failures:', dirResult.failed);
        }
      } else {
        // Single-file sample: smart-copy with dedup
        const result = await service.smartCopyFileTo(notebookPath, 'my_data');
        copiedNotebookPath = result.destPath;
        copyAction = result.action;
      }

      const notebook = await service.loadNotebook(copiedNotebookPath);
      if (loadCells(notebook, copiedNotebookPath)) {
        setSaveStatus('saved');
        const message = copyAction === 'copy-fresh'
          ? 'Copied to my_data \u2014 syncs to cloud automatically'
          : copyAction === 'open-modified'
            ? 'Opened your modified copy from my_data'
            : 'Opened existing copy from my_data';
        setSaveStatusMessage(message);
        setTimeout(() => setSaveStatusMessage(null), 4000);
        setFileExplorerRefreshTrigger(prev => prev + 1);
        service.setCwd(copiedNotebookPath).catch(err => console.warn('Failed to set CWD:', err));
        wasAutoOpenedRef.current = true;
        return;
      }
    } catch (err) {
      console.log('[auto-open] Smart copy failed, trying direct load:', err);
    }

    try {
      const notebook = await service.loadNotebook(notebookPath);
      if (loadCells(notebook, notebookPath)) {
        service.setCwd(notebookPath).catch(err => console.warn('Failed to set CWD:', err));
        wasAutoOpenedRef.current = true;
        return;
      }
    } catch (err2) {
      console.log('[auto-open] Direct load also failed:', err2);
    }

    setSaveStatusMessage(`Could not open "${notebookPath.split('/').pop()}". You can browse files in the explorer.`);
    setTimeout(() => setSaveStatusMessage(null), 6000);
  }, []);

  // Connect to WebSocket to get session details (including token)
  useEffect(() => {
    if (!notebookId) return;

    const connectToNotebook = async () => {
      try {
        const authToken = localStorage.getItem('authToken');
        if (!authToken) {
          setError('Not authenticated');
          setIsInitializing(false);
          return;
        }

        // Check if this is a new session that needs to be launched
        if (notebookId === 'new-session') {
          const launchParams = sessionStorage.getItem('notebook_launch_params');
          if (launchParams) {
            const params = JSON.parse(launchParams);
            sessionStorage.removeItem('notebook_launch_params'); // Clean up

            // Enrich with notebook_path from sessionStorage if not already set.
            // Handles the case where user navigated via NotebooksPage "Active Sessions"
            // button (which only passes instance_id) instead of the toast notification.
            if (!params.notebook_path) {
              params.notebook_path =
                sessionStorage.getItem(`pending_notebook_for_${params.instance_id}`)
                || sessionStorage.getItem('pending_sample_notebook')
                || undefined;
            }

            // Launch the notebook
            setLoadingStage('Launching notebook session...');
            const response = await fetch(`${getApiUrl()}/api/v1/notebooks/launch`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`
              },
              body: JSON.stringify(params),
            });

            if (!response.ok) {
              const error = await response.json();
              throw new Error(error.detail || 'Failed to launch notebook');
            }

            const notebook = await response.json();
            // Exhaustively check all sessionStorage sources for the notebook path.
            // The launch params may not include it if the user navigated here via
            // NotebooksPage "Active Sessions" instead of the toast notification.
            const nbPath = params.notebook_path
              || sessionStorage.getItem(`pending_notebook_for_${params.instance_id}`)
              || sessionStorage.getItem('pending_sample_notebook');
            if (nbPath) {
              pendingAutoOpenRef.current = nbPath;
              // Don't remove pending_notebook_for_* here — autoOpenNotebook
              // cleans up after successfully loading cells. Keeps the key
              // available if the WebSocket/Jupyter init times out and the
              // user retries from the sidebar or NotebooksPage.
            }
            // Navigate to the actual notebook session (client-side to preserve React state)
            navigate(`/dashboard/notebook/${notebook.id}`, { replace: true });
            return;
          }
        }

        // Check if we have a cached session token
        const existingToken = sessionStorage.getItem(`notebook_token_${notebookId}`);

        if (existingToken) {
          // Validate the existing session
          try {
            const authToken = localStorage.getItem('authToken');
            const response = await fetch(`${getApiUrl()}/api/v1/notebooks/proxy/${notebookId}/api/status`, {
              headers: {
                'Authorization': `Bearer ${authToken}`,
                'X-Session-Token': existingToken
              },
              credentials: 'include'
            });
            
            if (response.ok || response.status === 404) {
              // Token is valid (404 just means the status endpoint doesn't exist, but auth passed)
              // Existing session is valid, reuse it
              setJupyterToken(existingToken);
              setSessionId(notebookId);
              
              // Still need to connect to WebSocket to get instance details
              // but we already have the token, so this is just for metadata
              const wsUrl = getWebSocketUrl(`/api/v1/notebooks/session-ws/${notebookId}`);
              console.log('Attempting WebSocket connection (with existing token) to:', wsUrl);
              const ws = new WebSocket(wsUrl);
              
              ws.onopen = () => {
                // Connected to get instance details
                ws.send(JSON.stringify({ 
                  type: 'auth', 
                  token: authToken 
                }));
              };
              
              ws.onmessage = (event) => {
                const data = JSON.parse(event.data);
                if (data.status === 'ready' && data.instance_id) {
                  // Full ready message with cost/timing data
                  setInstanceDetails({
                    instanceId: data.instance_id,
                    instanceType: data.instance_type,
                    provider: data.provider || 'aws',
                    region: data.region || 'us-east-1',
                    hourlyCost: data.hourly_cost || 0,
                    launchTime: data.launch_time || null
                  });
                } else if (data.instance_id || data.instance_type) {
                  // Partial status message — set what we have, don't overwrite cost/timing
                  setInstanceDetails((prev: any) => ({
                    ...prev,
                    instanceId: data.instance_id || prev?.instanceId,
                    instanceType: data.instance_type || prev?.instanceType,
                    provider: data.provider || prev?.provider || 'aws',
                    region: data.region || prev?.region || 'us-east-1'
                  }));
                }
              };
              
              // Initialize the notebook service with existing token
              const service = getNotebookService({ 
                sessionId: notebookId,
                token: existingToken 
              });
              
              await service.initialize();
              
              // Monitor kernel status
              const statusInterval = setInterval(() => {
                const status = service.getKernelStatus();
                setKernelStatus(status || 'unknown');
              }, 2000);
              
              setIsInitializing(false);

              // Check for pending notebook to auto-open (cached-token fast path)
              // Extract instance_id from session ID format: notebook-{instance_id}-{user_id}
              const cachedInstanceId = notebookId?.replace(/^notebook-/, '').replace(/-\d+$/, '');
              const pendingPath = pendingAutoOpenRef.current
                || (cachedInstanceId && sessionStorage.getItem(`pending_notebook_for_${cachedInstanceId}`))
                || sessionStorage.getItem('pending_sample_notebook');
              if (pendingPath) {
                pendingAutoOpenRef.current = null;
                // Don't remove sessionStorage keys here — autoOpenNotebook
                // cleans up on successful load so retries still find them.
                const svc = getNotebookService({ sessionId: notebookId, token: existingToken });
                autoOpenNotebook(svc, pendingPath);
              } else {
                waitForMyData(service);
              }

              return () => clearInterval(statusInterval);
            } else if (response.status === 401) {
              // Token is invalid, clear it and get a new one
              // Token is invalid, clear it and get a new one
              sessionStorage.removeItem(`notebook_token_${notebookId}`);
            }
          } catch (err) {
            // Could not validate existing token, will get new one
            sessionStorage.removeItem(`notebook_token_${notebookId}`);
          }
        }

        // Connect to the notebook WebSocket to get session details.
        // Retry with exponential backoff if the initial connection fails
        // (common after backend restart while proxy/tunnel re-establishes).
        const MAX_WS_RETRIES = 5;
        let activeWs: WebSocket | null = null;
        let retryTimeout: ReturnType<typeof setTimeout> | null = null;
        let cancelled = false;

        const tryConnectWebSocket = (attempt: number) => {
          if (cancelled) return;

          const wsUrl = getWebSocketUrl(`/api/v1/notebooks/session-ws/${notebookId}`);
          console.log(`Attempting WebSocket connection (attempt ${attempt + 1}/${MAX_WS_RETRIES}) to:`, wsUrl);
          const ws = new WebSocket(wsUrl);
          activeWs = ws;

          ws.onopen = () => {
            // Connected to notebook WebSocket
            setLoadingStage('Authenticating...');
            // Send auth token
            ws.send(JSON.stringify({
              type: 'auth',
              token: authToken
            }));
          };

          ws.onmessage = async (event) => {
            const data = JSON.parse(event.data);
            console.log('WebSocket message received:', data);

            // Handle status updates
            if (data.type === 'status') {
              console.log(`Status update: ${data.stage} - ${data.message}`);
              setLoadingStage(data.message || 'Processing...');
              setCurrentStage(data.stage || 'processing');

              // Mark stages as completed - matches backend stage names
              const completionStages: Record<string, string[]> = {
                // Connection stages
                'tunnel_established': ['connection'],
                'tunnel_created': ['connection'],
                'tunnel_verified': ['connection'],
                'iap_tunnel_ready': ['connection'],
                // Jupyter stages
                'jupyter_ready': ['jupyter'],
                'jupyter_responding': ['jupyter'],
                // Auth stages
                'token_found': ['auth'],
                'token_retrieved': ['auth'],
                'gcp_token_retrieved': ['auth'],
                // Verification stages
                'jupyter_verified': ['jupyter', 'auth'],
                // Final stage
                'ready': ['connection', 'jupyter', 'auth']
              };

              if (completionStages[data.stage]) {
                setCompletedStages(prev => {
                  const newSet = new Set(prev);
                  completionStages[data.stage].forEach(s => newSet.add(s));
                  return newSet;
                });
              }

              // Update progress based on stage - ensure monotonic increase
              const progressMap: Record<string, number> = {
                // Initial stages (5-20%)
                'initializing': 5,
                'checking_instance': 8,
                'verifying_connection': 10,
                'testing_tunnel': 12,
                'preparing_tunnel': 15,
                'preparing_iap': 15,
                'creating_tunnel': 18,
                'creating_iap_tunnel': 18,
                'establishing_tunnel': 20,
                // Connection stages (20-40%)
                'connecting': 25,
                'waiting_ssm': 28,
                'tunnel_timeout': 30,
                'tunnel_failed': 30,
                'tunnel_error': 30,
                'iap_error': 30,
                'iap_tunnel_ready': 35,
                'tunnel_established': 35,
                'tunnel_created': 35,
                'tunnel_verified': 35,
                'initializing_ssm': 38,
                'waiting_for_instance': 40,
                // Jupyter stages (40-70%)
                'checking_jupyter': 45,
                'jupyter_starting': 50,
                'gcp_jupyter_starting': 50,
                'waiting_jupyter': 55,
                'jupyter_delayed': 60,
                'jupyter_responding': 65,
                'jupyter_ready': 70,
                // Token stages (70-95%)
                'retrieving_token': 75,
                'gcp_token_init': 75,
                'token_error': 75,
                'gcp_token_error': 75,
                'gcp_token_timeout': 75,
                'gcp_token_fetch': 78,
                'retrying_token': 80,
                'waiting': 80,
                'token_optional': 85,
                'token_found': 88,
                'token_retrieved': 90,
                'gcp_token_retrieved': 90,
                // Verification stages
                'verifying_jupyter': 92,
                'jupyter_verified': 94,
                // Final stage
                'ready': 95
              };

              const progress = progressMap[data.stage];
              if (progress !== undefined) {
                setLoadingProgress(prev => Math.max(progress, prev));
              }
              return;
            }

            // Handle errors
            if (data.type === 'error') {
              console.error('WebSocket error:', data.message);
              setError(data.message || 'Connection failed');
              setIsInitializing(false);
              ws.close();
              return;
            }

            // Handle ready status
            if (data.status === 'ready' && data.jupyter_url && data.token) {
              // Batch state updates: set progress to 100% first, then transition
              setLoadingProgress(100);
              setLoadingStage('Initializing Jupyter environment...');

              // Store token in sessionStorage for reconnection after backend restart
              sessionStorage.setItem(`notebook_token_${notebookId}`, data.token);

              // Initialize the notebook service with the token
              try {
                const service = getNotebookService({
                  sessionId: notebookId,
                  token: data.token
                });

                // Retry initialization with exponential backoff — Jupyter may
                // need a few seconds after the backend declares "ready".
                const MAX_INIT_RETRIES = 3;
                const INIT_DELAYS = [1000, 2000, 4000];
                let initSuccess = false;
                for (let attempt = 0; attempt <= MAX_INIT_RETRIES; attempt++) {
                  try {
                    await service.initialize();
                    initSuccess = true;
                    break;
                  } catch (initErr) {
                    if (attempt < MAX_INIT_RETRIES) {
                      setLoadingStage(`Connecting to Jupyter... (attempt ${attempt + 2}/${MAX_INIT_RETRIES + 1})`);
                      await new Promise(r => setTimeout(r, INIT_DELAYS[attempt]));
                      // Reset initialized flag so retry actually retries
                      service.resetInitialization();
                    } else {
                      throw initErr;
                    }
                  }
                }

                if (!initSuccess) {
                  throw new Error('Jupyter initialization failed after retries');
                }

                // Monitor kernel status
                const statusInterval = setInterval(() => {
                  const status = service.getKernelStatus();
                  setKernelStatus(status || 'unknown');
                }, 2000);

                // Store cleanup function
                ws.onclose = () => clearInterval(statusInterval);

                // Batch all ready-state updates in a single rAF so React renders atomically.
                // 300ms delay lets the progress bar visually reach 100% before we swap views.
                setTimeout(() => {
                  requestAnimationFrame(() => {
                    setJupyterToken(data.token);
                    setSessionId(notebookId);
                    setInstanceDetails({
                      instanceId: data.instance_id,
                      instanceType: data.instance_type,
                      provider: data.provider || 'aws',
                      region: data.region || 'us-east-1',
                      hourlyCost: data.hourly_cost || 0,
                      launchTime: data.launch_time || null
                    });
                    setIsInitializing(false);

                    // Auto-open notebook — check every possible source
                    const autoOpenPath = data.notebook_path
                      || pendingAutoOpenRef.current
                      || sessionStorage.getItem(`pending_notebook_for_${data.instance_id}`)
                      || sessionStorage.getItem('pending_sample_notebook');
                    if (autoOpenPath) {
                      pendingAutoOpenRef.current = null;
                      // Don't remove sessionStorage keys here — autoOpenNotebook
                      // cleans up on successful load so retries still find them.
                      autoOpenNotebook(service, autoOpenPath);
                    } else {
                      waitForMyData(service);
                    }
                  });
                }, 300);
              } catch (err) {
                console.error('Failed to initialize notebook service:', err);
                setError('Failed to initialize notebook service. Please refresh the page to try again.');
                setIsInitializing(false);
              }
            } else if (data.status === 'error') {
              setError(data.message || 'Failed to connect to notebook');
              setIsInitializing(false);
            }
          };

          ws.onerror = () => {
            console.error(`WebSocket error on attempt ${attempt + 1}/${MAX_WS_RETRIES}, URL: ${wsUrl}`);
            if (attempt + 1 < MAX_WS_RETRIES && !cancelled) {
              const delay = Math.pow(2, attempt) * 1000; // 1s, 2s, 4s, 8s
              setLoadingStage(`Connection interrupted, retrying in ${delay / 1000}s... (attempt ${attempt + 2}/${MAX_WS_RETRIES})`);
              retryTimeout = setTimeout(() => tryConnectWebSocket(attempt + 1), delay);
            } else {
              setError('Failed to connect to notebook session');
              setIsInitializing(false);
            }
          };

          ws.onclose = () => {
            // WebSocket closed
          };
        };

        tryConnectWebSocket(0);

        // Cleanup: close WebSocket and cancel pending retry on unmount or dependency change
        return () => {
          cancelled = true;
          if (retryTimeout) clearTimeout(retryTimeout);
          if (activeWs && activeWs.readyState <= WebSocket.OPEN) {
            activeWs.onclose = null; // prevent reconnect handlers
            activeWs.onerror = null;
            activeWs.close();
          }
        };
      } catch (err) {
        console.error('Failed to connect to notebook:', err);
        setError('Failed to connect to notebook session');
        setIsInitializing(false);
      }
    };

    connectToNotebook();
  }, [notebookId, autoOpenNotebook, waitForMyData, retryTick]);

  // Shared cell parser — converts raw Jupyter notebook JSON cells to NotebookCell[]
  const parseNotebookCells = useCallback((notebook: any): NotebookCell[] | null => {
    if (!notebook?.cells) return null;
    return notebook.cells.map((cell: any) => ({
      id: cell.metadata?.id || Math.random().toString(36).substring(2, 11),
      type: cell.cell_type === 'code' ? 'code' : cell.cell_type === 'raw' ? 'raw' : 'markdown',
      source: Array.isArray(cell.source) ? cell.source.join('') : cell.source,
      outputs: cell.outputs || [],
      execution_count: cell.execution_count || null,
      metadata: cell.metadata || {},
    }));
  }, []);

  // Build notebook JSON from cells (shared by save + save-as)
  const buildNotebookJson = useCallback((cellsToSave: NotebookCell[]) => ({
    cells: cellsToSave.map(cell => ({
      cell_type: cell.type,
      source: typeof cell.source === 'string' ? cell.source.split('\n').map((line, i, arr) =>
        i === arr.length - 1 ? line : line + '\n'
      ) : cell.source,
      metadata: { ...cell.metadata, id: cell.id },
      ...(cell.type === 'code' ? {
        outputs: cell.outputs || [],
        execution_count: cell.execution_count,
      } : {
        // raw and markdown cells: no outputs or execution_count in nbformat
      }),
    })),
    metadata: {
      kernelspec: {
        display_name: 'Python 3',
        language: 'python',
        name: 'python3',
      },
      language_info: {
        name: 'python',
        version: '3.9.0',
        mimetype: 'text/x-python',
        codemirror_mode: {
          name: 'ipython',
          version: 3
        },
        file_extension: '.py',
        pygments_lexer: 'ipython3',
      },
    },
    nbformat: 4,
    nbformat_minor: 5,
  }), []);

  // Load subdirectories under a given path for the Save As browser
  const loadSaveAsDirs = useCallback(async (dirPath: string) => {
    if (!sessionId || !jupyterToken) return;
    setSaveAsLoadingDirs(true);
    try {
      const service = getNotebookService({ sessionId, token: jupyterToken });
      const contents = await service.listFiles(dirPath);
      const dirs = (contents?.content || [])
        .filter((item: any) => item.type === 'directory')
        .map((item: any) => ({ name: item.name, path: item.path }))
        .sort((a: {name: string}, b: {name: string}) => a.name.localeCompare(b.name));
      setSaveAsDirs(dirs);
    } catch {
      setSaveAsDirs([]);
    } finally {
      setSaveAsLoadingDirs(false);
    }
  }, [sessionId, jupyterToken]);

  // save notebook with concurrency guard and exponential backoff
  const handleSaveNotebook = useCallback(async (cellsToSave: NotebookCell[]) => {
    if (!currentNotebook || !sessionId || !jupyterToken) return;
    if (isReadOnly) return;
    if (isSavingRef.current) return; // prevent concurrent saves

    isSavingRef.current = true;
    lastSaveCellsRef.current = cellsToSave;
    setSaveStatus('saving');

    const attemptSave = async () => {
      const service = getNotebookService({
        sessionId,
        token: jupyterToken
      });
      const notebook = buildNotebookJson(cellsToSave);
      await service.saveNotebook(currentNotebook, notebook);
    };

    const DELAYS = [1000, 2000, 4000]; // exponential backoff
    for (let attempt = 0; attempt <= DELAYS.length; attempt++) {
      try {
        await attemptSave();
        setSaveStatus('saved');
        isSavingRef.current = false;
        return;
      } catch (err) {
        if (attempt < DELAYS.length) {
          console.warn(`Save attempt ${attempt + 1} failed, retrying in ${DELAYS[attempt]}ms:`, err);
          await new Promise(r => setTimeout(r, DELAYS[attempt]));
        } else {
          console.error('Failed to save notebook after retries:', err);
          setSaveStatus('error');
          setSaveStatusMessage('Failed to save notebook.');
          isSavingRef.current = false;
        }
      }
    }
  }, [currentNotebook, sessionId, jupyterToken, isReadOnly, buildNotebookJson]);

  // --- Tab management helpers ---

  const addTab = useCallback((path: string, kind: NotebookTabKind = 'notebook') => {
    const title = path.split('/').pop() || path;
    setOpenTabs(prev => {
      if (prev.some(t => t.path === path)) return prev;
      return [...prev, { path, title, isDirty: false, kind }];
    });
    setActiveTabPath(path);
  }, []);

  const saveCurrentTabIfDirty = useCallback(async () => {
    if (!currentNotebook || !sessionId || !jupyterToken || isReadOnly) return;
    if (!liveCells.length) return;
    await handleSaveNotebook(liveCells);
  }, [currentNotebook, sessionId, jupyterToken, isReadOnly, liveCells, handleSaveNotebook]);

  const loadNotebookIntoView = useCallback(async (path: string, service: any) => {
    const notebook = await service.loadNotebook(path);
    const loadedCells = parseNotebookCells(notebook);
    if (loadedCells) {
      setCells(loadedCells);
      setCurrentNotebook(path);
      setActiveTabPath(path);
      setActiveTextFilePath(null);
      const pathIsReadOnly = path.startsWith('samples/');
      setSaveStatus(pathIsReadOnly ? 'idle' : 'saved');
      setSaveStatusMessage(null);
      service.setCwd(path).catch((err: any) => console.warn('Failed to set CWD:', err));
    }
  }, [parseNotebookCells]);

  const loadTextFileIntoView = useCallback(async (path: string, service: any) => {
    // TextFileEditor handles its own content fetch + save lifecycle; we just wire the tab.
    setCurrentNotebook(null);
    setCells([]);
    setActiveTextFilePath(path);
    setActiveTabPath(path);
    const pathIsReadOnly = path.startsWith('samples/');
    setSaveStatus(pathIsReadOnly ? 'idle' : 'saved');
    setSaveStatusMessage(null);
    service.setCwd(path).catch((err: any) => console.warn('Failed to set CWD:', err));
  }, []);

  const handleTabSelect = useCallback(async (path: string) => {
    if (path === activeTabPath) return;
    if (!sessionId || !jupyterToken) return;

    await saveCurrentTabIfDirty();

    const service = getNotebookService({ sessionId, token: jupyterToken });
    const targetTab = openTabs.find(t => t.path === path);
    const kind: NotebookTabKind = targetTab?.kind ?? 'notebook';
    try {
      if (kind === 'text') {
        await loadTextFileIntoView(path, service);
      } else {
        await loadNotebookIntoView(path, service);
      }
    } catch (err) {
      console.error('Failed to switch to tab:', path, err);
    }
  }, [activeTabPath, sessionId, jupyterToken, saveCurrentTabIfDirty, loadNotebookIntoView, loadTextFileIntoView, openTabs]);

  const handleTabClose = useCallback(async (path: string) => {
    if (path === activeTabPath) {
      await saveCurrentTabIfDirty();
    }

    setOpenTabs(prev => {
      const idx = prev.findIndex(t => t.path === path);
      const next = prev.filter(t => t.path !== path);

      if (path === activeTabPath && next.length > 0) {
        const newIdx = Math.min(idx, next.length - 1);
        const newActive = next[newIdx].path;
        setTimeout(() => handleTabSelect(newActive), 0);
      } else if (next.length === 0) {
        setCells([]);
        setCurrentNotebook(null);
        setActiveTabPath(null);
        setActiveTextFilePath(null);
      }

      return next;
    });
  }, [activeTabPath, saveCurrentTabIfDirty, handleTabSelect]);

  const handleTabDirtyChange = useCallback((isDirty: boolean) => {
    if (!activeTabPath) return;
    setOpenTabs(prev =>
      prev.map(t => t.path === activeTabPath ? { ...t, isDirty } : t)
    );
  }, [activeTabPath]);

  // handle file selection from explorer
  const handleFileSelect = useCallback(async (file: any) => {
    if (!jupyterToken) {
      setError('No Jupyter session token available');
      return;
    }
    if (file.type === 'directory') return;

    // Route text files (.md, .py, .yaml, etc.) through the TextFileEditor path.
    const isNotebook = file.type === 'notebook' || file.name?.endsWith?.('.ipynb');
    if (!isNotebook) {
      if (!file.name || !isEditableTextFile(file.name)) return; // unsupported binary → ignore
      // Already open? just switch
      if (openTabs.some(t => t.path === file.path)) {
        handleTabSelect(file.path);
        return;
      }
      await saveCurrentTabIfDirty();
      const service = getNotebookService({ sessionId: sessionId!, token: jupyterToken });

      // Sample text files are read-only in FUSE — smart-copy to my_data/ first.
      let targetPath: string = file.path;
      if (file.path.startsWith('samples/')) {
        try {
          const result = await service.smartCopyFileTo(file.path, 'my_data');
          targetPath = result.destPath;
          if (openTabs.some(t => t.path === targetPath)) {
            handleTabSelect(targetPath);
            return;
          }
          const message = result.action === 'copy-fresh'
            ? 'Copied to my_data — syncs to cloud automatically'
            : result.action === 'open-modified'
              ? 'Opened your modified copy from my_data'
              : 'Opened existing copy from my_data';
          setSaveStatusMessage(message);
          setTimeout(() => setSaveStatusMessage(null), 4000);
          setFileExplorerRefreshTrigger(prev => prev + 1);
        } catch (err) {
          console.warn('Failed to copy sample text file, opening read-only:', err);
        }
      }

      addTab(targetPath, 'text');
      try {
        await loadTextFileIntoView(targetPath, service);
      } catch (err) {
        console.error('Failed to open text file:', err);
      }
      return;
    }

    const service = getNotebookService({
      sessionId: sessionId!,
      token: jupyterToken
    });

    // If already open in a tab, just switch to it
    if (openTabs.some(t => t.path === file.path)) {
      handleTabSelect(file.path);
      return;
    }

    // Auto-save current tab before opening new one
    await saveCurrentTabIfDirty();

    // Auto-copy sample notebooks to my_data/ so users can edit and save
    if (file.path.startsWith('samples/')) {
      try {
        const segments = file.path.split('/');
        const isDirectorySample =
          segments[0] === 'samples' &&
          segments.length >= 3 &&
          segments[1] !== 'strategy_templates';

        let copiedPath: string;
        let copyAction: string;
        if (isDirectorySample) {
          const parentDir = segments.slice(0, 2).join('/');
          const filename = segments[segments.length - 1];
          const dirResult = await service.smartCopyDirectoryTo(parentDir, 'my_data');
          copiedPath = `${dirResult.destDir}/${filename}`;
          copyAction = dirResult.copied.length > 0 ? 'copy-fresh' : 'open-existing';
        } else {
          const result = await service.smartCopyFileTo(file.path, 'my_data');
          copiedPath = result.destPath;
          copyAction = result.action;
        }

        // Check if the copied path is already open
        if (openTabs.some(t => t.path === copiedPath)) {
          handleTabSelect(copiedPath);
          return;
        }

        const notebook = await service.loadNotebook(copiedPath);
        const loadedCells = parseNotebookCells(notebook);
        if (loadedCells) {
          setCells(loadedCells);
          setCurrentNotebook(copiedPath);
          addTab(copiedPath);
          setSaveStatus('saved');
          const message = copyAction === 'copy-fresh'
            ? 'Copied to my_data — syncs to cloud automatically'
            : copyAction === 'open-modified'
              ? 'Opened your modified copy from my_data'
              : 'Opened existing copy from my_data';
          setSaveStatusMessage(message);
          setTimeout(() => setSaveStatusMessage(null), 4000);
          setFileExplorerRefreshTrigger(prev => prev + 1);
          service.setCwd(copiedPath).catch(err => console.warn('Failed to set CWD:', err));
          return;
        }
      } catch (err) {
        console.warn('Failed to copy sample, loading read-only:', err);
      }
    }

    try {
      const notebook = await service.loadNotebook(file.path);
      const loadedCells = parseNotebookCells(notebook);
      if (loadedCells) {
        setCells(loadedCells);
        setCurrentNotebook(file.path);
        addTab(file.path);
        const pathIsReadOnly = file.path === 'samples' || file.path.startsWith('samples/');
        setSaveStatus(pathIsReadOnly ? 'idle' : 'saved');
        setSaveStatusMessage(null);
        service.setCwd(file.path).catch(err => console.warn('Failed to set CWD:', err));
      }
    } catch (err) {
      console.error('Failed to load notebook:', err);
      setError('Failed to load notebook');
    }
  }, [sessionId, jupyterToken, parseNotebookCells, openTabs, handleTabSelect, saveCurrentTabIfDirty, addTab, loadTextFileIntoView]);

  // handle new file creation
  const handleFileCreate = useCallback(async (path: string, type: string) => {
    if (type === 'notebook') {
      setCurrentNotebook(path);
      setCells([{
        id: Math.random().toString(36).substring(2, 11),
        type: 'code',
        source: '# New notebook\n',
        outputs: [],
        execution_count: null,
      }]);
    }
  }, []);

  // Save to Cloud (S3)
  const handleCloudSave = useCallback(async () => {
    if (!currentNotebook || !sessionId) return;
    setCloudSaveStatus('saving');
    try {
      const authToken = localStorage.getItem('authToken');
      const resp = await fetch(`${getApiUrl()}/api/v1/notebooks/save-to-cloud`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          session_id: sessionId,
          notebook_path: currentNotebook,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.detail || 'Cloud save failed');
      }
      setCloudSaveStatus('success');
      setTimeout(() => setCloudSaveStatus('idle'), 3000);
    } catch (err: any) {
      console.error('Cloud save failed:', err);
      setCloudSaveStatus('error');
      setSaveStatusMessage(err.message || 'Cloud save failed');
      setTimeout(() => { setCloudSaveStatus('idle'); setSaveStatusMessage(null); }, 5000);
    }
  }, [currentNotebook, sessionId]);

  // Copy read-only sample to workspace and switch to the writable copy.
  // For directory-based samples (e.g. samples/crypto_rsi_scanner/notebook.ipynb),
  // copies the entire parent directory so companion .py files come along.
  const handleCopyAndEdit = useCallback(async () => {
    if (!currentNotebook || !sessionId || !jupyterToken) return;
    try {
      const service = getNotebookService({ sessionId, token: jupyterToken });
      // Wait for my_data/ rclone mount (short backoff — instance already running)
      const BACKOFF = [500, 1000, 2000];
      for (let i = 0; i < BACKOFF.length; i++) {
        try {
          await service.listFiles('my_data');
          break;
        } catch {
          if (i === BACKOFF.length - 1) throw new Error('my_data directory not available');
          await new Promise(r => setTimeout(r, BACKOFF[i]));
        }
      }

      let copiedPath: string;
      let copyAction: string;

      // Detect directory-based samples: samples/<subdir>/notebook.ipynb
      // where subdir is NOT strategy_templates (those are single-file)
      const segments = currentNotebook.split('/');
      const isDirectorySample =
        segments[0] === 'samples' &&
        segments.length >= 3 &&
        segments[1] !== 'strategy_templates';

      if (isDirectorySample) {
        const parentDir = segments.slice(0, 2).join('/'); // e.g. "samples/crypto_rsi_scanner"
        const filename = segments[segments.length - 1];
        const dirResult = await service.smartCopyDirectoryTo(parentDir, 'my_data');
        copiedPath = `${dirResult.destDir}/${filename}`;
        copyAction = dirResult.copied.length > 0 ? 'copy-fresh' : 'open-existing';
        if (dirResult.failed.length > 0) {
          console.warn('[copy-and-edit] Partial directory copy failures:', dirResult.failed);
        }
      } else {
        const result = await service.smartCopyFileTo(currentNotebook, 'my_data');
        copiedPath = result.destPath;
        copyAction = result.action;
      }

      const notebook = await service.loadNotebook(copiedPath);
      if (notebook?.cells) {
        const loadedCells: NotebookCell[] = notebook.cells.map((cell: any) => ({
          id: cell.metadata?.id || Math.random().toString(36).substring(2, 11),
          type: cell.cell_type === 'code' ? 'code' : cell.cell_type === 'raw' ? 'raw' : 'markdown',
          source: Array.isArray(cell.source) ? cell.source.join('') : cell.source,
          outputs: cell.outputs || [],
          execution_count: cell.execution_count || null,
          metadata: cell.metadata || {},
        }));
        setCells(loadedCells);
        setCurrentNotebook(copiedPath);
        setSaveStatus('saved');
        const message = copyAction === 'copy-fresh'
          ? 'Copied to my_data \u2014 syncs to cloud automatically'
          : copyAction === 'open-modified'
            ? 'Opened your modified copy from my_data'
            : 'Opened existing copy from my_data';
        setSaveStatusMessage(message);
        setTimeout(() => setSaveStatusMessage(null), 4000);
        setFileExplorerRefreshTrigger(prev => prev + 1);
        service.setCwd(copiedPath).catch(err => console.warn('Failed to set CWD:', err));
      }
    } catch (err) {
      console.error('Copy & Edit failed:', err);
      setSaveStatusMessage('Failed to copy notebook. Please try again.');
      setTimeout(() => setSaveStatusMessage(null), 5000);
    }
  }, [currentNotebook, sessionId, jupyterToken]);

  // Save As — save current cells to the selected directory + filename
  const handleSaveAs = useCallback(async () => {
    if (!sessionId || !jupyterToken || !pendingSaveAsCellsRef.current || !saveAsFileName.trim()) return;
    // Don't allow saving into samples/
    if (saveAsBrowsePath === 'samples' || saveAsBrowsePath.startsWith('samples/')) return;
    // Ensure .ipynb extension
    const name = saveAsFileName.trim();
    const fileName = name.endsWith('.ipynb') ? name : name + '.ipynb';
    const finalPath = saveAsBrowsePath ? `${saveAsBrowsePath}/${fileName}` : fileName;

    try {
      const service = getNotebookService({ sessionId, token: jupyterToken });
      const notebook = buildNotebookJson(pendingSaveAsCellsRef.current);
      await service.saveNotebook(finalPath, notebook);
      setCurrentNotebook(finalPath);
      setSaveStatus('saved');
      setSaveStatusMessage(null);
      setShowSaveAsDialog(false);
      pendingSaveAsCellsRef.current = null;
    } catch (err) {
      console.error('Save As failed:', err);
      setSaveStatusMessage('Save As failed. Please try again.');
      setTimeout(() => setSaveStatusMessage(null), 5000);
    }
  }, [sessionId, jupyterToken, buildNotebookJson, saveAsBrowsePath, saveAsFileName]);

  if (isInitializing) {
    return (
      <div className={`min-h-screen bg-black flex items-center justify-center transition-opacity duration-300 ${loadingProgress >= 100 ? 'opacity-0' : 'opacity-100'}`}>
        <div className="text-center w-[28rem]">
          {/* Loading animation */}
          <div className="mb-8">
            <div className="relative w-24 h-24 mx-auto">
              <div className="absolute inset-0 border-4 border-gray-800 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-blue-500 rounded-full animate-spin" 
                   style={{ borderTopColor: 'transparent', borderRightColor: 'transparent' }}></div>
              <div className="absolute inset-2 border-4 border-purple-500 rounded-full animate-spin" 
                   style={{ borderBottomColor: 'transparent', borderLeftColor: 'transparent', animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
            </div>
          </div>
          
          {/* Status message with fixed height to prevent jumping */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-white">Preparing Your Notebook</h2>
            <div className="flex items-center justify-center space-x-2" style={{ minHeight: '28px', minWidth: '360px' }}>
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse flex-shrink-0"></div>
              <p className="text-gray-400 transition-opacity duration-200">{loadingStage}</p>
            </div>

            {/* Progress bar */}
            <div className="mt-6 mb-4">
              <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-blue-500 to-purple-500 h-full transition-all duration-300 ease-out"
                  style={{ width: `${loadingProgress}%`, willChange: 'width' }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2 text-center">{loadingProgress}% complete</p>
            </div>

            {/* Progress steps with stable layout */}
            <div className="mt-6 space-y-2 text-sm" style={{ minHeight: '96px' }}>
              {(() => {
                const connectionActive = ['initializing', 'checking_instance', 'verifying_connection', 'testing_tunnel', 'preparing_tunnel', 'preparing_iap', 'creating_tunnel', 'creating_iap_tunnel', 'establishing_tunnel', 'connecting', 'waiting_ssm', 'tunnel_established', 'initializing_ssm', 'waiting_for_instance', 'tunnel_failed', 'tunnel_error', 'iap_error'].includes(currentStage);
                const jupyterActive = ['checking_jupyter', 'waiting_jupyter', 'jupyter_delayed', 'jupyter_responding', 'jupyter_starting', 'gcp_jupyter_starting'].includes(currentStage);
                const authActive = ['retrieving_token', 'retrying_token', 'token_optional', 'token_found', 'gcp_token_init', 'gcp_token_fetch', 'gcp_token_retrieved', 'gcp_token_timeout', 'gcp_token_error', 'waiting', 'token_error'].includes(currentStage);

                const StepIcon = ({ completed, active }: { completed: boolean; active: boolean }) => (
                  <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
                    {completed ? (
                      <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none">
                        <path d="M3 8.5L6.5 12L13 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : active ? (
                      <svg className="w-4 h-4 animate-spin" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" opacity="0.25"/>
                        <path d="M14 8a6 6 0 00-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1"/>
                      </svg>
                    )}
                  </span>
                );

                return (
                  <>
                    <div className={`flex items-center space-x-2 transition-colors duration-200 ${
                      completedStages.has('connection') ? 'text-green-400' : connectionActive ? 'text-blue-400' : 'text-gray-600'
                    }`}>
                      <StepIcon completed={completedStages.has('connection')} active={connectionActive} />
                      <span>Establishing secure connection</span>
                    </div>

                    <div className={`flex items-center space-x-2 transition-colors duration-200 ${
                      completedStages.has('jupyter') ? 'text-green-400' : jupyterActive ? 'text-blue-400' : 'text-gray-600'
                    }`}>
                      <StepIcon completed={completedStages.has('jupyter')} active={jupyterActive} />
                      <span>Starting Jupyter environment</span>
                    </div>

                    <div className={`flex items-center space-x-2 transition-colors duration-200 ${
                      completedStages.has('auth') ? 'text-green-400' : authActive ? 'text-blue-400' : 'text-gray-600'
                    }`}>
                      <StepIcon completed={completedStages.has('auth')} active={authActive} />
                      <span>Authenticating session</span>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Helpful message based on stage — always-rendered container prevents reflow */}
            <div className="mt-6" style={{ minHeight: '72px' }}>
              <div className={`p-3 rounded-lg border transition-opacity duration-200 ${
                currentStage === 'waiting_jupyter' ? 'opacity-100 bg-blue-900/30 border-blue-800' :
                currentStage === 'waiting_for_instance' ? 'opacity-100 bg-yellow-900/30 border-yellow-800' :
                'opacity-0 border-transparent'
              }`}>
                {currentStage === 'waiting_jupyter' && (
                  <p className="text-xs text-blue-300">
                    First time loading? PropAlgos analytics tools are being installed on your instance.
                    This usually takes 1-2 minutes.
                  </p>
                )}
                {currentStage === 'waiting_for_instance' && (
                  <p className="text-xs text-yellow-300">
                    Your instance is still starting up. This is normal for new instances.
                    It should be ready in a minute or two.
                  </p>
                )}
              </div>
            </div>

            <div className="mt-4 text-center">
              <a
                href="/dashboard/research-feed"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-gray-500 hover:text-blue-400 transition-colors"
              >
                While you wait — read this week's quant research roll-up →
              </a>
            </div>

            {/* Instance info if available */}
            {instanceDetails && (
              <div className="mt-6 p-3 bg-gray-900 rounded-lg border border-gray-800">
                <p className="text-xs text-gray-500">Running on</p>
                <p className="text-sm text-white font-semibold">{instanceDetails.instanceType}</p>
                <p className="text-xs text-gray-400">{instanceDetails.provider?.toUpperCase()} • {instanceDetails.region}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-400 mb-4">{error}</div>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => {
                setRetrying(true);
                setError(null);
                setIsInitializing(true);
                setLoadingStage('Retrying connection...');
                setLoadingProgress(0);
                setCompletedStages(new Set());
                setCurrentStage('connecting');
                setRetryTick(tick => tick + 1);
                // retryinginess is cleared by the init effect transitioning isInitializing → false
                setTimeout(() => setRetrying(false), 2000);
              }}
              disabled={retrying}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed"
            >
              {retrying ? 'Retrying...' : 'Retry connection'}
            </button>
            <button
              onClick={() => navigate('/dashboard/notebooks')}
              className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700"
            >
              Back to Notebooks
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-black flex flex-col">
      {/* Status message toast (FUSE mount errors, copy failures, etc.) */}
      {saveStatusMessage && saveStatus !== 'saved' && saveStatus !== 'error' && (
        <div className="absolute top-16 right-4 z-50 bg-gray-800 text-gray-200 px-4 py-2 rounded-lg shadow-lg text-sm">
          {saveStatusMessage}
        </div>
      )}
      {/* Cloud save success toast */}
      {cloudSaveStatus === 'success' && (
        <div className="absolute top-16 right-4 z-50 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <span>Saved to cloud storage</span>
        </div>
      )}

      {/* header bar */}
      <div className="bg-gray-900 border-b border-gray-800 px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/dashboard/notebooks')}
              className="mr-4 text-gray-400 hover:text-white"
              title="Back to Notebooks"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="flex items-center space-x-4">
              <h1 className="text-lg font-semibold text-white">PropAlgos Notebook</h1>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" title="Session Active"></div>
                <span className="px-2 py-1 bg-green-800 text-green-200 text-xs rounded">Session Active</span>
              </div>
              {currentNotebook && (
                <div className="flex items-center space-x-3">
                  {/* Filename + directory path */}
                  <div className="flex items-center space-x-1.5">
                    <span className="text-sm text-white font-mono">
                      {currentNotebook.split('/').pop()}
                    </span>
                    {currentNotebook.includes('/') && (
                      <span className="text-xs text-gray-500">
                        {currentNotebook.substring(0, currentNotebook.lastIndexOf('/'))}
                      </span>
                    )}
                  </div>

                  {/* Read-only badge + Copy & Edit button */}
                  {isReadOnly ? (
                    <div className="flex items-center space-x-2">
                      <span className="flex items-center space-x-1 px-2 py-0.5 bg-yellow-900/40 text-yellow-300 text-xs rounded border border-yellow-700/40">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        <span>Read-only sample</span>
                      </span>
                      <button
                        onClick={handleCopyAndEdit}
                        className="px-2 py-0.5 text-xs bg-blue-600/60 text-blue-200 hover:bg-blue-600 hover:text-white rounded transition-colors"
                      >
                        Copy &amp; Edit
                      </button>
                    </div>
                  ) : (
                    /* Save status indicator for writable files */
                    saveStatus !== 'idle' && (
                      <div className="flex items-center space-x-1.5">
                        {saveStatus === 'saved' && (
                          <span className="flex items-center space-x-1 text-xs text-green-400">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span>Saved</span>
                          </span>
                        )}
                        {saveStatus === 'saving' && (
                          <span className="flex items-center space-x-1 text-xs text-blue-400">
                            <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.25"/>
                              <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            <span>Saving...</span>
                          </span>
                        )}
                        {saveStatus === 'error' && (
                          <span className="flex items-center space-x-1 text-xs text-red-400" title={saveStatusMessage || undefined}>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>Save failed</span>
                            <button
                              onClick={() => {
                                if (lastSaveCellsRef.current) {
                                  handleSaveNotebook(lastSaveCellsRef.current);
                                }
                              }}
                              className="ml-1.5 px-2 py-0.5 bg-red-800/50 hover:bg-red-700/60 text-red-200 rounded transition-colors"
                            >
                              Retry
                            </button>
                          </span>
                        )}
                        {saveStatusMessage && saveStatus === 'saved' && (
                          <span className="text-xs text-gray-500 ml-1">{saveStatusMessage}</span>
                        )}
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-6">
            {/* Instance Details */}
            {instanceDetails && (
              <div className="text-sm flex items-center space-x-4">
                <span className="text-gray-400" title="Instance Type">
                  <svg className="w-4 h-4 inline mr-1 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                  </svg>
                  <span className="font-semibold text-white">{instanceDetails.instanceType || 'Loading...'}</span>
                </span>
                <span className="text-gray-500" title="Provider and Region">
                  {instanceDetails.provider?.toUpperCase() || 'AWS'} • {instanceDetails.region || 'us-east-1'}
                </span>
                {instanceDetails.instanceId && (
                  <span className="text-gray-600 text-xs" title="Instance ID">
                    ({instanceDetails.instanceId})
                  </span>
                )}
              </div>
            )}
            
            {/* Kernel Status + Controls */}
            <div className="flex items-center space-x-2 text-sm text-gray-400">
              <span>Kernel:</span>
              <span className={`px-2 py-1 rounded text-xs ${
                kernelStatus === 'idle' ? 'bg-green-800 text-green-200' :
                kernelStatus === 'busy' ? 'bg-yellow-800 text-yellow-200' :
                'bg-gray-700 text-gray-300'
              }`}>
                {kernelStatus}
              </span>
              <button
                onClick={handleInterruptKernel}
                disabled={kernelStatus !== 'busy'}
                className="px-3 py-1.5 rounded text-xs bg-gray-700 hover:bg-yellow-700 text-gray-300 hover:text-yellow-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
                title="Interrupt Kernel"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <rect x="6" y="6" width="12" height="12" strokeWidth={2} strokeLinecap="round" />
                </svg>
                <span>Interrupt</span>
              </button>
              <button
                onClick={handleRestartKernel}
                disabled={!sessionId || kernelStatus === 'restarting'}
                className="px-3 py-1.5 rounded text-xs bg-gray-700 hover:bg-red-700 text-gray-300 hover:text-red-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
                title="Restart Kernel"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Restart</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* GPU Metrics Dashboard */}
      {instanceDetails && instanceDetails.instanceId && (
        <>
          <GPUMetricsHeader
            instanceId={instanceDetails.instanceId}
            instanceType={instanceDetails.instanceType}
            hourlyCost={instanceDetails.hourlyCost || 0}
            provider={instanceDetails.provider}
            region={instanceDetails.region}
            launchTime={instanceDetails.launchTime}
            aiSessionCostUsd={sessionUsage.costUsd}
            aiInputTokens={sessionUsage.inputTokens}
            aiOutputTokens={sessionUsage.outputTokens}
          />
        </>
      )}

      {/* main content */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {/* AI Assistant Cell - sits above notebook cells */}
        {sessionId && jupyterToken && (
          <AIAssistantCell
            onInsertCode={handleInsertAICode}
            defaultExpanded={false}
            phase={aiPhase}
            analysis={aiAnalysis}
            answers={aiAnswers}
            generatedCode={aiGeneratedCode}
            validationResult={aiValidationResult}
            error={aiError}
            isLoading={aiIsLoading}
            sessionUsage={sessionUsage}
            analyzeIntent={analyzeIntent}
            submitAnswer={submitAnswer}
            finishQuestioning={finishQuestioning}
            validateCode={validateCode}
            fixCode={fixCode}
            prepareForNotebook={prepareForNotebook}
            reset={resetAI}
          />
        )}

        {/* Notebook panels */}
        <div className="flex-1 overflow-hidden">
          <ThreePanelLayout
            controlRef={panelControlRef}
            leftPanel={{
              content: sessionId && jupyterToken ? (
                <FileExplorer
                  sessionId={sessionId}
                  token={jupyterToken}
                  onFileSelect={handleFileSelect}
                  onFileCreate={handleFileCreate}
                  refreshTrigger={fileExplorerRefreshTrigger}
                />
              ) : (
                <div className="p-4 text-gray-500">Waiting for session...</div>
              ),
              config: {
                defaultWidth: 250,
                minWidth: 200,
                maxWidth: 400,
                collapsible: true,
              },
              initialCollapsed: true,  // File explorer collapsed by default
            }}
            centerPanel={{
              content: sessionId && jupyterToken ? (
                <div className="flex flex-col h-full">
                  <NotebookTabBar
                    tabs={openTabs}
                    activeTabPath={activeTabPath}
                    onSelectTab={handleTabSelect}
                    onCloseTab={handleTabClose}
                    onOpenFileExplorer={() => panelControlRef.current?.toggleLeft()}
                  />
                  {activeTextFilePath ? (
                    <TextFileEditor
                      key={activeTextFilePath}
                      path={activeTextFilePath}
                      sessionId={sessionId}
                      token={jupyterToken}
                      isReadOnly={activeTextFilePath.startsWith('samples/')}
                      onDirtyChange={handleTabDirtyChange}
                      onSaveStatusChange={(status, message) => {
                        setSaveStatus(status === 'error' ? 'error' : status);
                        if (message) {
                          setSaveStatusMessage(message);
                          setTimeout(() => setSaveStatusMessage(null), 5000);
                        }
                      }}
                    />
                  ) : currentNotebook ? (
                    <CellManager
                      sessionId={sessionId}
                      token={jupyterToken}
                      initialCells={cells}
                      notebookPath={currentNotebook}
                      isReadOnly={isReadOnly}
                      onSave={currentNotebook && !isReadOnly ? handleSaveNotebook : undefined}
                      onSaveAs={currentNotebook && !isReadOnly ? (cellsToSave) => {
                        pendingSaveAsCellsRef.current = cellsToSave;
                        const fileName = currentNotebook.split('/').pop() || 'notebook.ipynb';
                        setSaveAsFileName(fileName);
                        const lastSlash = currentNotebook.lastIndexOf('/');
                        const currentDir = lastSlash > 0 ? currentNotebook.substring(0, lastSlash) : '';
                        const startPath = currentDir.startsWith('my_data') ? currentDir : 'my_data';
                        setSaveAsBrowsePath(startPath);
                        loadSaveAsDirs(startPath);
                        setShowSaveAsDialog(true);
                      } : undefined}
                      onCloudSave={currentNotebook && !isReadOnly ? handleCloudSave : undefined}
                      cloudSaveStatus={cloudSaveStatus}
                      onAddCellRef={(fn) => { addCellRef.current = fn; }}
                      onInterruptKernel={handleInterruptKernel}
                      onRestartKernel={handleRestartKernel}
                      kernelStatus={kernelStatus}
                      onCellsChange={setLiveCells}
                      onShowShortcuts={() => setShowShortcutsModal(true)}
                      onShowCommandPalette={() => setShowCommandPalette(true)}
                      onExecutionComplete={() => setVariableRefreshTrigger(n => n + 1)}
                      proxyAuthCode={proxyAuthCode}
                      getProxyAuthCode={getProxyAuthCode}
                      onDirtyChange={handleTabDirtyChange}
                    />
                  ) : (
                    <div className="flex-1 flex items-center justify-center text-gray-600">
                      <div className="text-center">
                        <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-sm">Open a file from the file explorer</p>
                        <button
                          onClick={() => panelControlRef.current?.toggleLeft()}
                          className="mt-2 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                        >
                          Open file explorer (Cmd+B)
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  No session initialized
                </div>
              ),
            }}
          rightPanel={{
            content: (
              <div className="h-full flex flex-col bg-black/50 backdrop-blur-sm border-l border-gray-800/50">
                {/* Tab bar */}
                <div className="flex border-b border-gray-800/50 flex-shrink-0">
                  {(['toc', 'variables', 'results'] as const).map(tab => (
                    <button
                      key={tab}
                      onClick={() => setRightPanelTab(tab)}
                      className={`px-3 py-2 text-xs uppercase tracking-wider transition-colors ${
                        rightPanelTab === tab
                          ? 'text-white border-b-2 border-blue-500'
                          : 'text-gray-500 hover:text-gray-300'
                      }`}
                    >
                      {tab === 'toc' ? 'Contents' : tab === 'variables' ? 'Variables' : 'Results'}
                    </button>
                  ))}
                </div>
                {/* Tab content */}
                <div className="flex-1 overflow-y-auto">
                  {rightPanelTab === 'toc' && (
                    <TableOfContents
                      cells={liveCells}
                      activeCellId={null}
                      onNavigateToCell={() => {}}
                    />
                  )}
                  {rightPanelTab === 'variables' && (
                    <VariableInspector
                      notebookService={activeNotebookService}
                      refreshTrigger={variableRefreshTrigger}
                    />
                  )}
                  {rightPanelTab === 'results' && (
                    <ResultsPanel
                      cells={liveCells}
                      sessionId={sessionId}
                      proxyAuthCode={proxyAuthCode}
                      getProxyAuthCode={getProxyAuthCode}
                      onNavigateToCell={(cellId) => {
                        document.getElementById(`cell-${cellId}`)?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'center',
                        });
                      }}
                    />
                  )}
                </div>
              </div>
            ),
            config: {
              defaultWidth: 400,
              minWidth: 300,
              maxWidth: 600,
              collapsible: true,
            },
            initialCollapsed: true,  // Charts panel is collapsed
          }}
        />
        </div>
      </div>
      {/* Save As dialog with directory browser */}
      {showSaveAsDialog && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-black/90 backdrop-blur-md p-6 w-[28rem] border border-gray-800/50 shadow-2xl">
            <h3 className="text-white font-semibold mb-1 text-lg">Save As</h3>
            <p className="text-xs text-green-400/80 mb-4">Files in my_data/ sync to cloud storage automatically.</p>

            {/* Save location breadcrumb */}
            <label className="text-sm text-gray-400 block mb-1.5">Save location</label>
            <div className="flex items-center gap-1 text-sm font-mono mb-2 flex-wrap">
              {saveAsBrowsePath.split('/').map((seg, i, arr) => {
                const segPath = arr.slice(0, i + 1).join('/');
                return (
                  <React.Fragment key={segPath}>
                    {i > 0 && <span className="text-gray-600">/</span>}
                    <button
                      onClick={() => {
                        setSaveAsBrowsePath(segPath);
                        loadSaveAsDirs(segPath);
                      }}
                      className="text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      {seg}
                    </button>
                  </React.Fragment>
                );
              })}
            </div>

            {/* Directory list */}
            <div className="border border-gray-700/50 bg-black/50 rounded max-h-40 overflow-y-auto mb-3">
              {saveAsLoadingDirs ? (
                <div className="flex items-center justify-center py-4 text-gray-500 text-sm">
                  <svg className="w-4 h-4 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.25"/>
                    <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  Loading...
                </div>
              ) : saveAsDirs.length === 0 ? (
                <div className="py-3 px-3 text-xs text-gray-600 italic">No subdirectories</div>
              ) : (
                saveAsDirs.map(dir => (
                  <button
                    key={dir.path}
                    onClick={() => {
                      setSaveAsBrowsePath(dir.path);
                      loadSaveAsDirs(dir.path);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors text-left"
                  >
                    <svg className="w-4 h-4 text-blue-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                    </svg>
                    {dir.name}
                  </button>
                ))
              )}
            </div>

            <div className="text-xs text-gray-500 mb-4">
              Saving into: <span className="font-mono text-gray-400">{saveAsBrowsePath}/</span>
            </div>

            {/* File name input */}
            <label className="text-sm text-gray-400 block mb-1.5">File name</label>
            <input
              type="text"
              value={saveAsFileName}
              onChange={(e) => setSaveAsFileName(e.target.value)}
              className="w-full px-3 py-2 bg-black/50 text-white border border-gray-700/50 focus:border-blue-500 focus:outline-none backdrop-blur-sm transition-all font-mono text-sm mb-2"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && saveAsFileName.trim()) handleSaveAs();
                if (e.key === 'Escape') { setShowSaveAsDialog(false); pendingSaveAsCellsRef.current = null; }
              }}
            />

            {/* Full path preview */}
            <div className="text-xs text-gray-500 mb-4">
              Full path: <span className="font-mono text-gray-400">
                {saveAsBrowsePath}/{saveAsFileName.trim() || '...'}{saveAsFileName.trim() && !saveAsFileName.trim().endsWith('.ipynb') ? '.ipynb' : ''}
              </span>
            </div>

            {(saveAsBrowsePath === 'samples' || saveAsBrowsePath.startsWith('samples/')) && (
              <p className="text-xs text-red-400 mb-3">Cannot save to the samples directory</p>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => { setShowSaveAsDialog(false); pendingSaveAsCellsRef.current = null; }}
                className="px-4 py-2 text-gray-400 hover:text-white bg-gray-800/30 hover:bg-gray-800/50 border border-gray-700/50 backdrop-blur-sm transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAs}
                disabled={!saveAsFileName.trim() || saveAsBrowsePath === 'samples' || saveAsBrowsePath.startsWith('samples/')}
                className="px-4 py-2 bg-blue-600/80 text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm transition-all shadow hover:shadow-blue-500/20"
              >
                Save
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      <CommandPalette
        commands={paletteCommands}
        isOpen={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
      />
      <ShortcutsModal
        isOpen={showShortcutsModal}
        onClose={() => setShowShortcutsModal(false)}
      />
    </div>
  );
};

export default NativeNotebookPage;